import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { contactGroupService } from 'contacts/services';
import { IContactGroup } from 'contacts/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { GetInfiniteQueryResult, IInfiniteQueryOptions } from 'core/types';
import { projectCustomerService } from 'pricing/services';
import {
  IAddProjectPricingProfileContactGroupPayload,
  IRemoveProjectPricingProfileContactGroupPayload,
  ISaveProjectPricingProfilePayload,
} from 'pricing/types';

interface IGetProjectCustomersProps {
  pricingProfileId: number;
  projectId: number;
}

interface IGetProjectCustomersResult extends GetInfiniteQueryResult<IContactGroup> {
  isAllCustomers: boolean;
}

export const useGetProjectCustomers = (
  { pricingProfileId, projectId }: IGetProjectCustomersProps,
  options: IInfiniteQueryOptions<IContactGroup> = { fetchAllPages: true }
): IGetProjectCustomersResult => {
  const result = useInfiniteQuery<IContactGroup>(
    (params, signal) =>
      contactGroupService.getContactGroups(
        { filters: { pricingProfileId, projectId }, ...params },
        signal
      ),
    'projectCustomers',
    { pricingProfileId, projectId },
    { ...options, enabled: !!pricingProfileId && !!projectId }
  );

  const isAllCustomers = useMemo(
    () => result.records.length === 1 && !result.records[0]?.id,
    [result.records]
  );

  return {
    ...result,
    records: isAllCustomers ? [] : result.records,
    count: isAllCustomers ? 0 : result.count,
    isAllCustomers,
  };
};

interface IProjectCustomerActionsProps {
  notificationPosition?: NotificationPosition;
}

export const useProjectCustomerActions = ({
  notificationPosition = NotificationPosition.FORM,
}: IProjectCustomerActionsProps = {}) => {
  return {
    ...useToggleAllCustomers(notificationPosition),
    ...useAddProjectCustomer(notificationPosition),
    ...useRemoveProjectCustomer(notificationPosition),
  };
};

const useToggleAllCustomers = (notificationPosition: NotificationPosition) => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messageToggleAllCustomersSuccess') },
    loading: { message: t('messageToggleAllCustomersInProgress') },
    error: { title: t('errorTitleToggleAllCustomers') },
    position: notificationPosition,
    invalidateQueries: ['projectCustomers'],
  });

  const { mutateAsync } = useMutation(
    (payload: ISaveProjectPricingProfilePayload) =>
      projectCustomerService.saveProjectPricingProfile(payload),
    { ...errors }
  );

  return { toggleAllCustomers: mutateAsync };
};

const useAddProjectCustomer = (notificationPosition: NotificationPosition) => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messageProjectCustomerAddSuccess') },
    loading: { message: t('messageProjectCustomerAddInProgress') },
    error: { title: t('errorTitleProjectCustomerAdd') },
    position: notificationPosition,
    invalidateQueries: ['projectCustomers'],
  });

  const { mutateAsync } = useMutation(
    (payload: IAddProjectPricingProfileContactGroupPayload) =>
      projectCustomerService.addProjectPricingProfileContactGroup(payload),
    { ...errors }
  );

  return { addProjectCustomer: mutateAsync };
};

const useRemoveProjectCustomer = (notificationPosition: NotificationPosition) => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messageProjectCustomerRemoveSuccess') },
    loading: { message: t('messageProjectCustomerRemoveInProgress') },
    error: { title: t('errorTitleProjectCustomerRemove') },
    position: notificationPosition,
    invalidateQueries: ['projectCustomers'],
  });

  const { mutateAsync } = useMutation(
    (payload: IRemoveProjectPricingProfileContactGroupPayload) =>
      projectCustomerService.removeProjectPricingProfileContactGroup(payload),
    { ...errors }
  );

  return { removeProjectCustomer: mutateAsync };
};
